import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faDownload, faPencilAlt, faTrash, faExpand,
  faFileAlt, faSpinner, faExclamationTriangle, faImage, faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import { openPdfInline } from '../../shared/PdfViewer/PdfViewerHost';

// P1-24: side-panel inline preview. Renders a PDF inline with pdfjs (via the
// shared PDF viewer host) when expanded, otherwise just metadata; renders
// an <img> for image MIME types; for everything else, shows metadata + a
// download CTA.
const DocumentsPreviewPanel = ({
  doc, propertyId, onClose, onDownload, onEdit, onDelete, isOwner,
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setBlobUrl(null);
    setMissing(false);
    if (!doc) return;
    const isImage = (doc.mime_type || '').startsWith('image/');
    if (!isImage) return;

    let cancelled = false;
    setLoading(true);
    const previewPath = doc.source === 'contractor' && doc.download_url
      ? doc.download_url
      : `/api/properties/${propertyId}/documents/${doc.id}/download`;
    fetch(getApiEndpoint(previewPath), {
      headers: getAuthHeader(),
    })
      .then(async r => {
        if (cancelled) return;
        if (r.status === 410) { setMissing(true); return; }
        if (!r.ok) throw new Error('Failed to load preview');
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setMissing(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
    };
  }, [doc, propertyId]);

  // Revoke blob URL when it changes or the panel unmounts.
  useEffect(() => {
    if (!blobUrl) return undefined;
    return () => { try { window.URL.revokeObjectURL(blobUrl); } catch (_) {} };
  }, [blobUrl]);

  // ESC closes the panel.
  useEffect(() => {
    if (!doc) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, onClose]);

  if (!doc) return null;

  const isImage = (doc.mime_type || '').startsWith('image/');
  const isPdf = (doc.original_filename || '').toLowerCase().endsWith('.pdf')
             || (doc.mime_type || '').toLowerCase() === 'application/pdf';

  const openFull = async () => {
    try {
      const fullPath = doc.source === 'contractor' && doc.download_url
        ? doc.download_url
        : `/api/properties/${propertyId}/documents/${doc.id}/download`;
      const r = await fetch(
        getApiEndpoint(fullPath),
        { headers: getAuthHeader() }
      );
      if (r.status === 410) { setMissing(true); return; }
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      if (isPdf) {
        openPdfInline(url, doc.original_filename || doc.name || 'Document');
        // Viewer keeps the blob alive while open; revoke after a long delay.
        setTimeout(() => { try { window.URL.revokeObjectURL(url); } catch (_) {} }, 5 * 60 * 1000);
      } else {
        const w = window.open(url, '_blank');
        if (w) w.onload = () => setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (_) { toast.error('Failed to open document'); }
  };

  const friendlySize = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const displayCategory = doc.type || 'Other';

  return (
    <>
      <div className="docs-preview-backdrop" onClick={onClose} />
      <aside className="docs-preview-panel" role="dialog" aria-label="Document preview">
        <div className="docs-preview-head">
          <div className="docs-preview-head-icon">
            <FontAwesomeIcon icon={isImage ? faImage : isPdf ? faFilePdf : faFileAlt} />
          </div>
          <div className="docs-preview-head-text">
            <h3 className="docs-preview-title" title={doc.name}>{doc.name}</h3>
            <div className="docs-preview-sub">
              <span className="docs-preview-cat">{displayCategory}</span>
              <span className="docs-preview-sep">·</span>
              <span>{friendlySize(doc.file_size)}</span>
              {doc.zone && (<>
                <span className="docs-preview-sep">·</span>
                <span>{doc.zone}</span>
              </>)}
              {doc.source === 'contractor' && (
                <span
                  title="Uploaded by the contractor — read-only here"
                  style={{
                    marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                    background: 'rgba(37,99,235,.12)', color: '#1d4ed8', border: '1px solid rgba(37,99,235,.25)',
                  }}
                >
                  {doc.source_ref || 'Contractor'}
                </span>
              )}
            </div>
          </div>
          <button className="docs-preview-close" onClick={onClose} aria-label="Close preview">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="docs-preview-body">
          {missing ? (
            <div className="docs-preview-missing">
              <FontAwesomeIcon icon={faExclamationTriangle} className="docs-preview-missing-icon" />
              <h4>File missing</h4>
              <p>The document record exists but the file is no longer on disk. Re-upload to restore it.</p>
            </div>
          ) : isImage ? (
            <div className="docs-preview-image-wrap">
              {loading ? (
                <div className="docs-preview-loading">
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading preview…
                </div>
              ) : blobUrl ? (
                <img src={blobUrl} alt={doc.name} className="docs-preview-image" />
              ) : (
                <div className="docs-preview-loading">No preview available</div>
              )}
            </div>
          ) : isPdf ? (
            <div className="docs-preview-doc-meta">
              <div className="docs-preview-doc-icon"><FontAwesomeIcon icon={faFilePdf} /></div>
              <p>PDF document — open the full viewer to read, page through, and zoom.</p>
              <button type="button" className="docs-preview-open-btn" onClick={openFull}>
                <FontAwesomeIcon icon={faExpand} /> Open full viewer
              </button>
            </div>
          ) : (
            <div className="docs-preview-doc-meta">
              <div className="docs-preview-doc-icon"><FontAwesomeIcon icon={faFileAlt} /></div>
              <p>This file type can&apos;t be previewed inline. Download to view.</p>
            </div>
          )}

          {doc.summary && (
            <div className="docs-preview-notes">
              <h4>Notes</h4>
              <p>{doc.summary}</p>
            </div>
          )}

          <dl className="docs-preview-meta-list">
            <div>
              <dt>Filename</dt>
              <dd title={doc.original_filename}>{doc.original_filename || '—'}</dd>
            </div>
            <div>
              <dt>Uploaded</dt>
              <dd>{doc.created_at ? new Date(doc.created_at).toLocaleString() : '—'}</dd>
            </div>
            {doc.uploaded_by_name && (
              <div>
                <dt>By</dt>
                <dd>{doc.uploaded_by_name}</dd>
              </div>
            )}
            {doc.expiry_date && (
              <div>
                <dt>Expires</dt>
                <dd>{new Date(doc.expiry_date).toLocaleDateString()}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="docs-preview-actions">
          <button type="button" className="docs-preview-btn docs-preview-btn-primary" onClick={onDownload}>
            <FontAwesomeIcon icon={faDownload} /> Download
          </button>
          {isOwner && (
            <button type="button" className="docs-preview-btn" onClick={onEdit}>
              <FontAwesomeIcon icon={faPencilAlt} /> Edit
            </button>
          )}
          {isOwner && (
            <button type="button" className="docs-preview-btn docs-preview-btn-danger" onClick={onDelete}>
              <FontAwesomeIcon icon={faTrash} /> Delete
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default DocumentsPreviewPanel;
